import type {
  CareerDraftEvent,
  CareerEvent,
  CareerHistoryState,
  CareerTradeEvent,
  GameState,
  PlayerHistoryProvenance,
  SeasonSummary,
  TradeOffer,
} from './types'
import { buildLadder } from './season'

export const PLAYER_APPEARANCE_MILESTONES = [10, 50, 100, 150, 200, 250, 300] as const

export const createCareerHistory = (): CareerHistoryState => ({
  events: [],
  seasons: [],
  playerProvenance: { version: 1, appearanceBaselines: {} },
})

const capturePlayerProvenance = (state: GameState): PlayerHistoryProvenance => ({
  version: 1,
  appearanceBaselines: Object.fromEntries(state.club.players.map(player => [player.id, {
    seniorAppearances: player.development?.seniorMatches ?? 0,
    matchExposureEntries: player.development?.history?.filter(entry => entry.matchExposure).length ?? 0,
  }])),
})

const hasPlayerProvenance = (history: CareerHistoryState | undefined) => Boolean(
  history?.playerProvenance?.version === 1
  && history.playerProvenance.appearanceBaselines
  && typeof history.playerProvenance.appearanceBaselines === 'object',
)

const migrateHistoryProvenance = (state: GameState, history: CareerHistoryState): CareerHistoryState => hasPlayerProvenance(history)
  ? history
  : { ...history, playerProvenance: capturePlayerProvenance(state) }

const upsertEvents = (history: CareerHistoryState, incoming: CareerEvent[]): CareerHistoryState => {
  const byId = new Map(history.events.map(event => [event.id, event]))
  for (const event of incoming) byId.set(event.id, event)
  return { ...history, events: [...byId.values()].sort((a, b) => a.id.localeCompare(b.id)) }
}

const seasonOutcome = (position: number, teamCount: number) => {
  if (position === 1) return 'Premiers'
  if (position <= Math.ceil(teamCount / 2)) return `Top ${Math.ceil(teamCount / 2)}`
  return 'Building season'
}

const amendSummaryFromEvents = (history: CareerHistoryState, season: number): CareerHistoryState => {
  const existing = history.seasons.find(summary => summary.season === season)
  if (!existing) return history
  const acceptedTrades = history.events
    .filter((event): event is CareerTradeEvent => event.type === 'trade-accepted' && event.season === season)
    .map(event => ({ playerId: event.playerId, playerName: event.playerName, destinationClubId: event.destinationClubId, acquiredPick: event.acquiredPick }))
    .sort((a, b) => a.playerId.localeCompare(b.playerId))
  const draftSelections = history.events
    .filter((event): event is CareerDraftEvent => event.type === 'draft-selection' && event.season === season)
    .map(event => ({ pick: event.pick, prospectId: event.prospectId, playerId: event.playerId, playerName: event.playerName }))
    .sort((a, b) => a.pick - b.pick)
  const delistings = history.events
    .filter((event): event is Extract<CareerEvent, { type: 'delisting-confirmed' }> => event.type === 'delisting-confirmed' && event.season === season)
    .map(event => ({ playerId: event.playerId, playerName: event.playerName }))
    .sort((a, b) => a.playerId.localeCompare(b.playerId))
  const amended: SeasonSummary = { ...existing, acceptedTrades, draftSelections, delistings }
  return { ...history, seasons: history.seasons.map(summary => summary.season === season ? amended : summary) }
}

const syncCompletedSeason = (state: GameState, history: CareerHistoryState): CareerHistoryState => {
  if (!state.season || state.season.fixtures.length === 0) return history
  const finalRound = Math.max(...state.season.fixtures.map(fixture => fixture.round))
  if (state.season.completedThroughRound < finalRound || state.season.finals?.status !== 'complete') return history
  const ladder = buildLadder(state.season, finalRound)
  const position = ladder.findIndex(row => row.teamId === state.season!.userTeamId) + 1
  const kingsport = ladder[position - 1]
  if (!kingsport || !ladder[0]) return history
  const summary: SeasonSummary = {
    id: `season:${state.season.season}`,
    season: state.season.season,
    kingsport: { wins: kingsport.wins, losses: kingsport.losses, draws: kingsport.draws, position },
    premier: {
      teamId: state.season.finals.premierTeamId!,
      name: state.season.teams[state.season.finals.premierTeamId!].name,
    },
    finalLadder: ladder.map(row => ({ ...row })),
    acceptedTrades: [],
    draftSelections: [],
    delistings: [],
    outcome: seasonOutcome(position, ladder.length),
  }
  const seasons = history.seasons.some(item => item.season === summary.season)
    ? history.seasons.map(item => item.season === summary.season ? { ...summary, acceptedTrades: item.acceptedTrades, draftSelections: item.draftSelections, delistings: item.delistings } : item)
    : [...history.seasons, summary].sort((a, b) => a.season - b.season)
  const withCompletion = upsertEvents({ ...history, seasons }, [{ id: `season:${summary.season}:completed`, type: 'season-completed', season: summary.season }])
  return amendSummaryFromEvents(withCompletion, summary.season)
}

export const hydrateCareerHistory = (state: GameState, saved?: CareerHistoryState): CareerHistoryState => {
  const hasSavedHistory = Boolean(saved && Array.isArray(saved.events) && Array.isArray(saved.seasons))
  const history = migrateHistoryProvenance(state, hasSavedHistory
    ? saved!
    : { ...createCareerHistory(), playerProvenance: capturePlayerProvenance(state) })
  return syncCareerHistory({ ...state, history }).history
}

export const syncCareerHistory = (state: GameState, derivePlayerEvents = true): GameState => {
  const base = migrateHistoryProvenance(state, state.history ?? createCareerHistory())
  const existingEventIds = new Set(base.events.map(event => event.id))
  const seasonNumber = state.season?.season ?? state.recruitment.season
  const matchEvents: CareerEvent[] = state.season
    ? Object.values(state.season.results).map(result => ({
        id: `season:${state.season!.season}:match:${result.fixtureId}`,
        type: 'match-result' as const,
        season: state.season!.season,
        round: result.round,
        fixtureId: result.fixtureId,
        homeTeamId: result.homeTeamId,
        awayTeamId: result.awayTeamId,
        homeScore: result.homeScore.total,
        awayScore: result.awayScore.total,
      }))
    : []
  const playerEvents: CareerEvent[] = derivePlayerEvents ? state.club.players.flatMap(player => {
    const exposures = player.development?.history?.filter(entry => entry.matchExposure) ?? []
    const careerStartsInSimulation = player.id === 'ari-dane' || player.draftedSeason !== undefined
    if (!careerStartsInSimulation || exposures.length === 0 || player.development.seniorMatches === 0) return []
    const baseline = base.playerProvenance.appearanceBaselines[player.id] ?? {
      seniorAppearances: 0,
      matchExposureEntries: 0,
    }
    const trackedExposures = exposures.slice(baseline.matchExposureEntries)
    const events: CareerEvent[] = []
    const debutId = `player:${player.id}:debut`
    if (baseline.seniorAppearances === 0 && trackedExposures.length > 0 && !existingEventIds.has(debutId)) events.push({
      id: debutId,
      type: 'player-debut',
      season: seasonNumber,
      round: trackedExposures[0].round,
      playerId: player.id,
      playerName: player.name,
      seniorAppearances: 1,
    })
    for (const milestone of PLAYER_APPEARANCE_MILESTONES) {
      const trackedAppearancesRequired = milestone - baseline.seniorAppearances
      if (trackedAppearancesRequired <= 0
        || player.development.seniorMatches < milestone
        || trackedExposures.length < trackedAppearancesRequired) continue
      const milestoneId = `player:${player.id}:milestone:${milestone}`
      if (existingEventIds.has(milestoneId)) continue
      events.push({
        id: milestoneId,
        type: 'player-milestone',
        season: seasonNumber,
        round: trackedExposures[trackedAppearancesRequired - 1].round,
        playerId: player.id,
        playerName: player.name,
        seniorAppearances: milestone,
      })
    }
    return events
  }) : []
  const draftEvents: CareerEvent[] = state.season
    ? state.recruitment.draft.order.flatMap(pick => {
        if (pick.clubId !== state.season!.userTeamId || !pick.prospectId) return []
        const prospect = state.recruitment.prospects.find(item => item.id === pick.prospectId)
        if (!prospect) return []
        return [{
          id: `season:${state.recruitment.season}:draft:${pick.overall}`,
          type: 'draft-selection' as const,
          season: state.recruitment.season,
          pick: pick.overall,
          clubId: pick.clubId,
          prospectId: prospect.id,
          playerId: `draftee-${prospect.id}`,
          playerName: prospect.name,
        }]
      })
    : []
  let history = upsertEvents(base, [...matchEvents, ...playerEvents, ...draftEvents])
  history = syncCompletedSeason(state, history)
  history = amendSummaryFromEvents(history, state.recruitment.season)
  return { ...state, history }
}

export const recordAcceptedTrade = (state: GameState, offer: TradeOffer): CareerHistoryState => {
  const player = state.club.players.find(item => item.id === offer.playerId)
  if (!player) return state.history ?? createCareerHistory()
  let history = upsertEvents(syncCareerHistory(state).history, [{
    id: `season:${state.recruitment.season}:trade:${offer.id}`,
    type: 'trade-accepted',
    season: state.recruitment.season,
    playerId: player.id,
    playerName: player.name,
    destinationClubId: offer.clubId,
    acquiredPick: offer.pickOverall,
  }])
  history = amendSummaryFromEvents(history, state.recruitment.season)
  return history
}

export const recordSeasonTransition = (state: GameState, delistedPlayerIds: string[], nextSeason: number): CareerHistoryState => {
  const completedSeason = state.recruitment.season
  const delistings: CareerEvent[] = delistedPlayerIds.flatMap(playerId => {
    const player = state.club.players.find(item => item.id === playerId)
    return player ? [{
      id: `season:${completedSeason}:delisted:${player.id}`,
      type: 'delisting-confirmed' as const,
      season: completedSeason,
      playerId: player.id,
      playerName: player.name,
    }] : []
  })
  let history = syncCareerHistory(state).history
  history = upsertEvents(history, [
    ...delistings,
    { id: `season:${completedSeason}:transition:${nextSeason}`, type: 'season-transition', season: completedSeason, nextSeason },
  ])
  return amendSummaryFromEvents(history, completedSeason)
}
